import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useAtomValue } from "jotai";
import { Dispatch, MouseEvent, SetStateAction, useState } from "react";
import {
  Event,
  IconMeetingMap,
  STATUS_ICON_MAP,
  STATUS_TEXT_MAP
} from "./types";

import LockIcon from "@mui/icons-material/Lock";
import { useTheme } from "@mui/material/styles";
import IwSwitch from "../../../components/app/IwSwitch";
import { RECORDING_INDICATOR_STYLES } from "../../../components/discovery/editor/RecordPlayer";

import { MODIFY_MEETING_ERROR, RECURRING_ERROR, ZOOM_MEETING_HOSTED_STATUS } from "../../../constants";
import { baseAtom } from "../../../stores/jotai/base";
import {
  HandleToggleChangeProps,
  ToggleEventPayload
} from "./GoogleAndZoomLoggedIn";
import { debounce } from "lodash";
import { MeetingListAction } from "./MeetingList";
import { useGetEventByZoomUserUUIDAndMeetingCode } from "../../../tanstackQuery/social-integrations/handle-zoom";
import { useKeycloak } from "@react-keycloak/web";
import ZoomEventModal from "./Modal/ZoomEventModal";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";

type ZoomDayEventProps = {
  event: Event;
  zoomUserUUID:string;
  handlers: {
    setOpenInterviewCreateModal: Dispatch<SetStateAction<boolean>>;
    setToggleEventPayload: Dispatch<SetStateAction<ToggleEventPayload | null>>;
    handleToggleChange: ({}: HandleToggleChangeProps) => Promise<void>;
    handleOpenMeetingDetailsModal: (
      _event: MouseEvent<HTMLDivElement | HTMLButtonElement>
    ) => void;
  };
};

export function ZoomDayEvent({ event, handlers, zoomUserUUID }: ZoomDayEventProps) {
  const theme = useTheme();
  const base = useAtomValue(baseAtom);
  const { keycloak } = useKeycloak();
  const session = useAtomValue(discoverySessionAtom);
  const [openZoomEventModal,setZoomEventModal] = useState(false)
  const [alertModalTitle, setAlertModalTitle] = useState("");
  const [alertModalDescription, setAlertModalDescription] = useState("");
  const [showSetupInterviewInfo, setShowSetupInterviewInfo] = useState(base.user?.userPreference?.skipZoomToggleInterviewSetupTip)

  const {
    handleToggleChange,
    setOpenInterviewCreateModal,
    setToggleEventPayload,
    handleOpenMeetingDetailsModal
  } = handlers;

  const isRecurringEvent = event?.zoomMeetingDetails?.isRecurringEvent

  // const isUpcomming = event?.meetingStatus === "upcoming";
  const isInterviewProcessing = event?.meetingStatus === "meetingOngoing";
  const isMeetingRecording = event?.meetingStatus === "meetingRecording";
  // const isZoomProcessingTheRecording = event?.meetingStatus === "zoomProcessingCloudRecording";

  const hasEnableZoomMeeting = event?.shouldBeRecorded // && (isUpcomming || isInterviewProcessing || isMeetingRecording || isZoomProcessingTheRecording);
  const IconComponent = STATUS_ICON_MAP[event?.meetingStatus] || null;
  const InterviewProgressIcon = STATUS_ICON_MAP["meetingOngoing"] || null;

  const IconMeeting = IconMeetingMap[event?.conferenceType] || null;
  const isMeetingLocked =
    event?.isZoomMeeting &&
    event?.shouldBeRecorded &&
    base.team.id !== event.appTeamID;

  const debouncedMeetingToggle = debounce((event) => {
    handleToggleChange({
      meetingId: event?.meetingId,
      shouldBeRecorded: event.shouldBeRecorded,
      startTime: event.startTime,
      appInterviewID: event.appInterviewID,
    });
  }, 300);
    const {
      data,
      isLoading,
      refetch: fetchZoomEvent,
    } = useGetEventByZoomUserUUIDAndMeetingCode(
      { accessToken: keycloak.token },
      { zoomUserUUID, meetingCode: event?.meetingId },
      { enabled: false, retry: 1 }
    );

  const handleMeetingToggle = () => {
    const hasPermissionToToggle =
      !isRecurringEvent && event?.isZoomMeeting ;

    if (hasPermissionToToggle && !isMeetingLocked && event?.appInterviewID) {
      if(!hasEnableZoomMeeting){
       setAlertModalTitle("Interview Already Created For This Zoom Meeting");
       setAlertModalDescription(
         "An interview is already set up for this meeting.You can update it if needed."
       );
       setShowSetupInterviewInfo(false)
       setZoomEventModal(!openZoomEventModal);
      }
      debouncedMeetingToggle(event);
    }
  };
  
  return (
    <Box
      key={event?.id}
      pr={4}
      py={1}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        ":hover": {
          backgroundColor: theme.custom.hoverOptionColor,
        },
      }}
      onClick={async () => {
        const hasPermissionToToggle =
          !isRecurringEvent && event?.isZoomMeeting;
        if (
           event?.zoomMeetingDetails?.hostedStatus ===
           ZOOM_MEETING_HOSTED_STATUS.NOT_DETERMINED
         ) {
          const result = await fetchZoomEvent();
           // @ts-ignore
           if (result?.error?.response?.data?.statusCode === 403) {
             setAlertModalTitle(
               "This Zoom meeting is owned by a different user."
             );
             setShowSetupInterviewInfo(false)
             setZoomEventModal(!openZoomEventModal);
             return;
           }
         }
         if (
           event?.zoomMeetingDetails?.hostedStatus ===
           ZOOM_MEETING_HOSTED_STATUS.EXTERNAL || event?.zoomMeetingDetails?.hostedStatus ===
           ZOOM_MEETING_HOSTED_STATUS.UNKNOWN
         ) {
           setAlertModalTitle(
               "This Zoom meeting is owned by a different user."
             );
             setShowSetupInterviewInfo(false)
             setZoomEventModal(!openZoomEventModal);
             return;
         }
         if (!base.user?.userPreference?.skipZoomToggleInterviewSetupTip && !event.appInterviewID) {
           setAlertModalTitle(
             "Let’s set up a new interview for this Zoom meeting"
           );
           setAlertModalDescription(
             "Please choose a template and enter the interviewee’s name.This will help guide the interview and make note-taking easier.After the meeting, we’ll handle the recording, generate insights, and link everything to your business model"
           );
           setShowSetupInterviewInfo(true)
           setZoomEventModal(!openZoomEventModal);
         }

          if (hasPermissionToToggle && !isMeetingLocked) {
          if (event && !event.appInterviewID) {
            setOpenInterviewCreateModal(true);
            setToggleEventPayload({
              meetingId: event?.meetingId,
              topic: event.title,
              shouldBeRecorded: event.shouldBeRecorded,
              startTime: event.startTime,
              appInterviewID: null,
              meetingLink: event.meetingUrl,
              meetingPassword: event.meetingPassword,
              conferenceType: event.conferenceType,
              formattedTime: event.formattedTime,
              description: event.description,
              eventLink: event.eventLink,
              phone: event.phone,
              email:event.attendees
            });
            return;
          }

          // await handleToggleChange({
          //   meetingId: event.meetingId,
          //   shouldBeRecorded: event.shouldBeRecorded,
          //   startTime: event.startTime,
          //   appInterviewID: event.appInterviewID
          // });
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0
        }}
      >
        <ZoomEventModal
           alertModalTitle={alertModalTitle}
           setZoomEventModal={setZoomEventModal}
           openZoomEventModal={openZoomEventModal}
           alertModalDescription={alertModalDescription}
           setAlertModalDescription={setAlertModalDescription}
           setAlertModalTitle={setAlertModalTitle}
           event={event}
           showSetupInterviewInfo={showSetupInterviewInfo}
           setShowSetupInterviewInfo={setShowSetupInterviewInfo}
        />
        <Typography
          pl={7}
          sx={{
            fontWeight: "600",
            color:
              isInterviewProcessing ||
              event?.meetingStatus === "meetingRecording" ||
              event?.meetingStatus === "meetingOngoing"
                ? theme.custom.mainButtonHighlight
                : "#595959",
          }}
        >
          {event?.title}
        </Typography>
        <Box
          pl={7}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            color: "#A1A1AA"
          }}
        >
          <span>{event?.time}</span>
          {event?.meetingUrl ? (
            <>
              <span>|</span>
              <Box
                component="div"
                onClick={(_event) => {
                  _event.stopPropagation();
                  handleOpenMeetingDetailsModal(_event);
                  setToggleEventPayload({
                    meetingId: event?.meetingId,
                    topic: event.title,
                    shouldBeRecorded: event.shouldBeRecorded,
                    startTime: event.startTime,
                    appInterviewID: null,
                    meetingLink: event.meetingUrl,
                    meetingPassword: event.meetingPassword,
                    conferenceType: event.conferenceType,
                    formattedTime: event.formattedTime,
                    description: event.description,
                    eventLink: event.eventLink,
                    phone: event.phone,
                    email:event.attendees
                  });
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#A1A1AA",
                  textDecoration: "none",

                  "&:hover": {
                    textDecoration: "underline",
                    color: theme.custom.mainButtonHighlight
                  }
                }}
              >
                {IconMeeting ? <IconMeeting width={18} height={18} /> : null}
                <span>Join Meeting</span>
              </Box>
            </>
          ) : null}
          {
            event?.appInterviewID && event?.isZoomMeeting && event?.selfHosted && !isMeetingLocked &&<>
              <span>|</span>
              <MeetingListAction meeting={event} />
            </>
          }
        </Box>
        {hasEnableZoomMeeting ? (
          <>
            <Box
              pl={2.9}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                color: isInterviewProcessing
                  ? theme.custom.mainButtonHighlight
                  : "#595959"
              }}
            >
              {event?.meetingStatus ? (
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    bgcolor: theme.custom.gray6,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "50%",
                    color: isInterviewProcessing
                      ? theme.custom.mainButtonHighlight
                      : "#828282",
                  }}
                >
                  {isMeetingRecording && InterviewProgressIcon ? (
                    <InterviewProgressIcon
                      width={14}
                      style={{ color: theme.custom.mainButtonHighlight }}
                    />
                  ) : IconComponent ? (
                    <IconComponent width={14} />
                  ) : null}
                </Box>
              ) : null}

              {isMeetingRecording ? (
                <>
                  <span style={{ color: theme.custom.mainButtonHighlight }}>
                    Interview in Progress
                  </span>
                  <span style={{ color: "#A1A1AA" }}>|</span>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Typography
                      fontSize={15}
                      color={theme.custom.recordingIndicatorColor}
                    >
                      Recording...
                    </Typography>
                    <Box component="div" sx={RECORDING_INDICATOR_STYLES}></Box>
                  </Box>
                </>
              ) : (
                <span>{STATUS_TEXT_MAP[event?.meetingStatus]}</span>
              )}
            </Box>
          </>
        ) : null}
      </Box>
      {isRecurringEvent ? (
        <Tooltip title={RECURRING_ERROR}>
          <IconButton>
            <LockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : isMeetingLocked ? (
        <Tooltip title={MODIFY_MEETING_ERROR}>
          <IconButton>
            <LockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : event?.isZoomMeeting ? (
        <IwSwitch
          size="small"
          checked={event.shouldBeRecorded}
          onChange={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleMeetingToggle();
            return;
          }}
        />
      ) : event?.isZoomMeeting ? (
        <Tooltip title="Not supported for non self hosted meeting">
          <IconButton>
            <LockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Not supported for non-Zoom meetings">
          <IconButton>
            <LockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
